/**
 * A comment box where "@" offers the people who can see this document.
 *
 * THE LIST IS A SUGGESTION, NOT A CONTROL. The server re-checks every name at the write
 * (_resolve_mentions in cres-aws-t11), because what this sends is a claim in a request body. So
 * this file may be as helpful as it likes -- narrowing, searching, guessing -- without any of that
 * being load-bearing.
 *
 * WHAT IT REMEMBERS IS WHAT WAS PICKED, not what was typed. The message stays plain text and the
 * identifiers ride beside it; nothing is parsed back out of the sentence later, which is what keeps
 * a mention working after somebody is renamed.
 */
import React from "react";
import { Box, CircularProgress, List, ListItemButton, Paper, Popper, TextField, Typography } from "@mui/material";

import { mentionQuery, withMention, stillNamed } from "./mentions";
import type { Person } from "./mentions";

const Grey = "#525968";

export default function MentionField({ value, onChange, chosen, onChosen, people, placeholder,
                                       autoFocus, disabled, minRows, onSubmit }: {
    value: string;
    onChange: (text: string) => void;
    chosen: Person[];
    onChosen: (people: Person[]) => void;
    /** Asks the server who may be named here. Debounced by the caller if it wants to be. */
    people: (search: string) => Promise<Person[]>;
    placeholder?: string;
    autoFocus?: boolean;
    disabled?: boolean;
    minRows?: number;
    onSubmit?: () => void;
}) {
    const input = React.useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
    const [caret, setCaret] = React.useState(0);
    const [offers, setOffers] = React.useState<Person[] | null>(null);
    const [looking, setLooking] = React.useState(false);
    const [highlighted, setHighlighted] = React.useState(0);

    const query = React.useMemo(() => mentionQuery(value, caret), [value, caret]);

    // ONE REQUEST PER PAUSE, not per keystroke. The picker is a convenience and must not be a load
    // on the tier that serves the document everybody is reading.
    React.useEffect(() => {
        if (!query) { setOffers(null); return; }
        let cancelled = false;
        setLooking(true);
        const timer = setTimeout(async () => {
            try {
                const found = await people(query.query);
                if (!cancelled) { setOffers(found); setHighlighted(0); }
            } catch {
                if (!cancelled) setOffers([]);   // a picker that cannot load is not an error to show
            } finally {
                if (!cancelled) setLooking(false);
            }
        }, 180);
        return () => { cancelled = true; clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query?.query]);

    const take = (person: Person) => {
        const next = withMention(value, caret, person);
        onChange(next.text);
        onChosen(stillNamed(next.text, [...chosen, person]));
        setOffers(null);
        requestAnimationFrame(() => {
            input.current?.focus();
            input.current?.setSelectionRange(next.caret, next.caret);
            setCaret(next.caret);
        });
    };

    const open = !!query && !!offers?.length;

    // A PORTAL, NOT AN ABSOLUTELY-POSITIONED BOX. Inside the comment DIALOG the list was clipped
    // by DialogContent's own overflow -- five people were offered and one and a half were visible.
    // A Popper escapes the dialog's box; `anchor` is what it hangs off.
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);

    return (
        <Box sx={{ position: "relative" }} ref={setAnchor}>
            <TextField
                inputRef={input}
                fullWidth multiline size="small"
                minRows={minRows ?? 2}
                autoFocus={autoFocus}
                disabled={disabled}
                placeholder={placeholder ?? "Type @ to name somebody"}
                value={value}
                onChange={(e) => {
                    onChange(e.target.value);
                    setCaret(e.target.selectionStart ?? e.target.value.length);
                    // A NAME DELETED IS A MENTION WITHDRAWN, checked as they type rather than only
                    // at submit, so the chips below never claim somebody the sentence does not.
                    onChosen(stillNamed(e.target.value, chosen));
                }}
                onSelect={(e: any) => setCaret(e.target.selectionStart ?? 0)}
                onKeyDown={(e) => {
                    if (open) {
                        if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setHighlighted((h) => Math.min(h + 1, (offers?.length ?? 1) - 1));
                            return;
                        }
                        if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setHighlighted((h) => Math.max(h - 1, 0));
                            return;
                        }
                        if (e.key === "Enter" || e.key === "Tab") {
                            e.preventDefault();
                            const person = offers?.[highlighted];
                            if (person) take(person);
                            return;
                        }
                        if (e.key === "Escape") { e.preventDefault(); setOffers(null); return; }
                    }
                    // ENTER SENDS, SHIFT+ENTER BREAKS THE LINE -- but only once the picker is shut,
                    // or choosing somebody would post the half-written comment.
                    if (e.key === "Enter" && !e.shiftKey && onSubmit) {
                        e.preventDefault();
                        onSubmit();
                    }
                }}
                slotProps={{ htmlInput: { style: { fontSize: 13 } } }}/>

            <Popper open={!!query && (looking || !!offers)} anchorEl={anchor} placement="bottom-start"
                    // ABOVE THE DIALOG, which sits at 1300 in MUI's stack.
                    sx={{ zIndex: 1400, width: anchor?.clientWidth }}
                    modifiers={[{ name: "flip", enabled: true },
                                { name: "preventOverflow", options: { padding: 8 } }]}>
                <Paper elevation={3} sx={{ mt: 0.5, maxHeight: 220, overflowY: "auto" }}>
                    {looking && !offers?.length && (
                        <Box sx={{ p: 1.25, display: "flex", alignItems: "center", gap: 1 }}>
                            <CircularProgress size={14}/>
                            <Typography sx={{ fontSize: 12, color: Grey }}>Looking…</Typography>
                        </Box>
                    )}
                    {!looking && offers && !offers.length && (
                        <Typography sx={{ fontSize: 12, color: Grey, p: 1.25 }}>
                            Nobody here by that name who can see this document.
                        </Typography>
                    )}
                    <List dense disablePadding>
                        {(offers ?? []).map((person, index) => (
                            <ListItemButton key={person.AccountUserUSID}
                                            selected={index === highlighted}
                                            onMouseDown={(e) => { e.preventDefault(); take(person); }}
                                            sx={{ py: 0.5 }}>
                                <Box>
                                    <Typography sx={{ fontSize: 13 }}>{person.Name}</Typography>
                                    {person.Email && (
                                        <Typography sx={{ fontSize: 11, color: Grey }}>
                                            {person.Email}
                                        </Typography>
                                    )}
                                </Box>
                            </ListItemButton>
                        ))}
                    </List>
                </Paper>
            </Popper>
        </Box>
    );
}
